"use client";

import React, { useState, useEffect } from "react";
import { signIn } from "next-auth/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Link from "next/link";
import { UnifiedModal } from "@/components/modal";
import { PasswordInput } from "@/components/ui/password-input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAnalytics } from "@/hooks/use-analytics";
import {
  userAuthSchema,
  type UserAuthSchema,
  userRegisterSchema,
  type UserRegisterSchema,
} from "@/types/auth";
import { toast } from "sonner";
import { TermsAcceptanceCheckbox } from "@/components/legal/terms-acceptance-checkbox";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: "signin" | "signup";
  onSuccess?: () => void;
  zIndex?: string | number;
}

/**
 * Auth Modal - Sign In / Sign Up
 *
 * Fully refactored to use Priscilla's unified 
 * .usland-glass utility framework and semantic design tokens.
 */
export function AuthModal({
  isOpen,
  onClose,
  defaultTab = "signin",
  onSuccess,
  zIndex = 'var(--z-modal)',
}: AuthModalProps) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { trackModalOpen, trackModalClose, trackButtonClick } = useAnalytics();

  useEffect(() => {
    if (isOpen) {
      trackModalOpen(activeTab === "signin" ? "signin-modal" : "signup-modal");
    }
  }, [isOpen, activeTab, trackModalOpen]);

  const handleClose = () => {
    trackModalClose(activeTab === "signin" ? "signin-modal" : "signup-modal");
    setActiveTab(defaultTab);
    setError(null);
    onClose();
  };

  const handleGoogleSignIn = () => {
    setIsPending(true);
    trackButtonClick("google-signin");
    signIn("google", { callbackUrl: "/dashboard" });
  };

  const signInForm = useForm<UserAuthSchema>({
    resolver: zodResolver(userAuthSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSignInSubmit = (data: UserAuthSchema) => {
    setError(null);
    setIsPending(true);

    signIn("credentials", {
      email: data.email,
      password: data.password,
      redirect: false,
    })
      .then((result) => {
        if (result?.error) {
          setError("Invalid email or password");
          setIsPending(false);
        } else if (result?.ok) {
          onClose();
          setTimeout(() => {
            onSuccess?.();
            window.location.href = "/dashboard";
          }, 100);
        }
      })
      .catch((error) => {
        console.error("Sign in error:", error);
        setError("An error occurred during sign in. Please try again.");
        setIsPending(false);
      });
  };

  const signUpForm = useForm<UserRegisterSchema>({
    resolver: zodResolver(userRegisterSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      mobilePhone: "",
      password: "",
      confirmPassword: "",
      planId: 1,
      invitationToken: undefined,
      agreeToTerms: false,
    },
  });

  const handleSignUpSubmit = (data: UserRegisterSchema) => {
    setError(null);
    setIsPending(true);

    fetch("/api/auth/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.user) {
          toast.success("Account created! Signing you in...");
          return signIn("credentials", {
            email: data.email,
            password: data.password,
            redirect: false,
          });
        } else {
          throw new Error(result.error || "Failed to create account");
        }
      })
      .then((signInResult) => {
        if (signInResult?.error) {
          throw new Error("Failed to sign in");
        }
        onClose();
        setTimeout(() => {
          onSuccess?.();
          window.location.href = "/dashboard";
        }, 100);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "An error occurred");
        setIsPending(false);
      });
  };

  return (
    <UnifiedModal
      open={isOpen}
      onOpenChange={handleClose}
      title="Authentication"
      size="md"
      showCloseButton={true}
      className="max-w-[480px] border usland-glass shadow-2xl rounded-lg"
    >
      <div style= {{ zIndex }} className="relative px-6 pt-6">
        {/* Navigation Tabs built with design system references */}
        <div className="mb-6 bg-transparent text-center">
          <div className="mb-6 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => {
                setActiveTab("signin");
                setError(null);
              }}
              className={cn(
                "rounded-md px-6 py-2.5 text-sm font-bold transition-all duration-150 border",
                activeTab === "signin"
                  ? "bg-[rgb(var(--primary-default))] text-white border-transparent shadow-md"
                  : "border-gray-200 bg-white/40 text-gray-600 hover:bg-white/80 hover:border-gray-300",
              )}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("signup");
                setError(null);
              }}
              className={cn(
                "rounded-md px-6 py-2.5 text-sm font-bold transition-all duration-150 border",
                activeTab === "signup"
                  ? "bg-[rgb(var(--primary-default))] text-white border-transparent shadow-md"
                  : "border-gray-200 bg-white/40 text-gray-600 hover:bg-white/80 hover:border-gray-300",
              )}
            >
              Sign Up
            </button>
          </div>

          <h2 className="text-[rgb(var(--primary-default))] mb-1.5 text-2xl font-black tracking-tight">
            {activeTab === "signin" ? "Welcome Back" : "Join USLand"}
          </h2>
          <p className="text-gray-500 font-semibold text-sm">
            {activeTab === "signin"
              ? "Sign in to access your properties and listings"
              : "Discover commercial real estate opportunities"}
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6 border-red-200 bg-red-50 text-red-900 font-medium">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* OAuth Interface Stack */}
      <button
        type="button"
        className={cn(
          "relative flex h-12 w-full items-center justify-center font-bold text-sm text-gray-700",
          "border border-gray-200 bg-white/80 hover:bg-white",
          "rounded-md transition-all duration-150 shadow-sm hover:shadow-md",
        )}
        onClick={handleGoogleSignIn}
        disabled={isPending}
      >
        <svg className="absolute left-4 h-5 w-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span className="ml-6">Continue with Google</span>
      </button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-200/80" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="text-gray-400 bg-transparent px-3 font-semibold tracking-wider text-[11px]">
            Or continue with email
          </span>
        </div>
      </div>

      {activeTab === "signin" ? (
        <Form {...signInForm}>
          <form
            onSubmit={signInForm.handleSubmit(handleSignInSubmit)}
            className="space-y-4"
          >
            <FormField
              control={signInForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700 text-xs font-bold uppercase tracking-wider">
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="name@company.com"
                      disabled={isPending}
                      autoCapitalize="none"
                      autoComplete="email"
                      className="h-11 rounded-md border-gray-200 bg-white/60 font-medium text-gray-900 focus:bg-white transition-all"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={signInForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel className="text-gray-700 text-xs font-bold uppercase tracking-wider">
                      Password
                    </FormLabel>
                    <Link
                      href="/forgot-password"
                      className="text-[rgb(var(--primary-default))] text-xs font-bold hover:underline"
                    >
                      Forgot?
                    </Link>
                  </div>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter your password"
                      disabled={isPending}
                      autoComplete="current-password"
                      className="h-11 rounded-md border-gray-200 bg-white/60 font-medium text-gray-900 focus:bg-white transition-all"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isPending}
              className="h-11 w-full font-bold uppercase tracking-wider text-xs rounded-md bg-[rgb(var(--primary-default))] text-white hover:bg-[rgb(var(--primary-hover))] transition-all shadow-md flex items-center justify-center gap-2"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </form>
        </Form>
      ) : (
        <Form {...signUpForm}>
          <form
            onSubmit={signUpForm.handleSubmit(handleSignUpSubmit)}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={signUpForm.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 text-xs font-bold uppercase tracking-wider">
                      First Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John"
                        disabled={isPending}
                        autoComplete="given-name"
                        className="h-11 rounded-md border-gray-200 bg-white/60 font-medium text-gray-900 focus:bg-white transition-all"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={signUpForm.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 text-xs font-bold uppercase tracking-wider">
                      Last Name
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Doe"
                        disabled={isPending}
                        autoComplete="family-name"
                        className="h-11 rounded-md border-gray-200 bg-white/60 font-medium text-gray-900 focus:bg-white transition-all"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={signUpForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700 text-xs font-bold uppercase tracking-wider">
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="name@company.com"
                      disabled={isPending}
                      autoCapitalize="none"
                      autoComplete="email"
                      className="h-11 rounded-md border-gray-200 bg-white/60 font-medium text-gray-900 focus:bg-white transition-all"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={signUpForm.control}
              name="mobilePhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700 text-xs font-bold uppercase tracking-wider">
                    Mobile Phone <span className="text-gray-400 font-medium lowercase italic">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      disabled={isPending}
                      autoComplete="tel"
                      className="h-11 rounded-md border-gray-200 bg-white/60 font-medium text-gray-900 focus:bg-white transition-all"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={signUpForm.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 text-xs font-bold uppercase tracking-wider">
                      Password
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder="••••••••"
                        disabled={isPending}
                        autoComplete="new-password"
                        className="h-11 rounded-md border-gray-200 bg-white/60 font-medium text-gray-900 focus:bg-white transition-all"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={signUpForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 text-xs font-bold uppercase tracking-wider">
                      Confirm
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder="••••••••"
                        disabled={isPending}
                        autoComplete="new-password"
                        className="h-11 rounded-md border-gray-200 bg-white/60 font-medium text-gray-900 focus:bg-white transition-all"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={signUpForm.control}
              name="agreeToTerms"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <TermsAcceptanceCheckbox
                      checked={field.value || false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isPending}
              className="h-11 w-full font-bold uppercase tracking-wider text-xs rounded-md bg-[rgb(var(--primary-default))] text-white hover:bg-[rgb(var(--primary-hover))] transition-all shadow-md flex items-center justify-center gap-2"
            >
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Account
            </Button>
          </form>
        </Form>
      )}

      {/* Security Status Frame */}
      <div className="mt-6 flex items-center justify-center gap-2 border-t border-gray-200/60 pt-6">
        <Shield className="text-[rgb(var(--primary-default))] h-4 w-4 opacity-70" />
        <p className="text-gray-400 font-semibold text-xs">
          Your information is secure and encrypted
        </p>
      </div>
    </UnifiedModal>
  );
}